import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { apiClient } from '../api'
import { useToast } from './ToastContext'
import { useSettings } from './SettingsContext'

const SessionContext = createContext(null)

export function SessionProvider({ children }) {
  const [sessions, setSessions] = useState([])
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [logs, setLogs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [streamMode, setStreamMode] = useState('offline') // 'websocket' | 'sse' | 'polling' | 'offline'
  const [lastUpdate, setLastUpdate] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const { showToast } = useToast()
  const { settings } = useSettings()
  
  const pollIntervalRef = useRef(null)
  const sessionsPollRef = useRef(null)
  const logsRef = useRef(logs)
  const currentSessionIdRef = useRef(currentSessionId)

  // Keep refs updated
  useEffect(() => {
    logsRef.current = logs
  }, [logs])

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId
  }, [currentSessionId])

  // Load sessions
  const loadSessions = useCallback(async (useCache = true) => {
    try {
      const result = await apiClient.fetchSessions(0, 50, useCache)
      const nextSessions = result?.sessions || []
      setSessions(nextSessions)
      setIsConnected(true)
      setLastUpdate(new Date())
      return nextSessions
    } catch (error) {
      setIsConnected(false)
      console.error('Session load error:', error)
      return []
    }
  }, [])

  // Load logs for a session
  const loadSessionLogs = useCallback(async (sessionId) => {
    if (!sessionId) return

    setIsLoading(true)
    try {
      const result = await apiClient.fetchSessionLogs(sessionId, { limit: 150 })
      const logsArray = result?.logs || []
      if (Array.isArray(logsArray)) {
        setLogs(logsArray)
        setHasMore(result?.hasMore || false)
        setIsConnected(true)
        setLastUpdate(new Date())
      }
    } catch (error) {
      if (error.message?.includes('not found')) {
        showToast('Session not found', 'error')
      } else {
        showToast('Failed to load logs', 'error')
      }
      setIsConnected(false)
      console.error('Log load error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [showToast])

  // Load older logs (keyset pagination on upward scroll)
  const loadMoreLogs = useCallback(async () => {
    if (!currentSessionIdRef.current || isLoading || isLoadingMore || !hasMore) return 0

    setIsLoadingMore(true)
    try {
      const earliestLog = logsRef.current[0]
      const lastId = earliestLog ? earliestLog.id : null

      if (lastId) {
        const result = await apiClient.fetchSessionLogs(currentSessionIdRef.current, { lastId, limit: 100 })
        const newLogs = result?.logs || []
        if (newLogs.length > 0) {
          setLogs(prev => [...newLogs, ...prev])
          setHasMore(result?.hasMore || false)
          return newLogs.length
        } else {
          setHasMore(false)
        }
      }
      return 0
    } catch (error) {
      console.error('Load more error:', error)
      return 0
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoading, isLoadingMore, hasMore])

  // Select a session
  const selectSession = useCallback(async (sessionId) => {
    setCurrentSessionId(sessionId)
    if (sessionId) {
      await loadSessionLogs(sessionId)
    } else {
      setLogs([])
      setHasMore(false)
    }
  }, [loadSessionLogs])

  // Refresh current session
  const refresh = useCallback(async () => {
    if (!currentSessionId) {
      showToast('No session selected', 'warning')
      return
    }

    try {
      apiClient.clearCache()
      await loadSessionLogs(currentSessionId)
      await loadSessions(false)
      showToast('Refreshed', 'success')
    } catch {
      showToast('Refresh failed', 'error')
    }
  }, [currentSessionId, loadSessionLogs, loadSessions, showToast])

  // Delete a single session
  const deleteSession = useCallback(async (sessionId) => {
    if (!sessionId) return false

    const previousSessions = sessions
    const previousCurrentSessionId = currentSessionId
    const wasCurrentSession = sessionId === currentSessionId
    const nextSessions = sessions.filter(session => session.session_id !== sessionId)

    // Optimistic update
    setSessions(nextSessions)

    if (wasCurrentSession) {
      const nextSessionId = nextSessions[0]?.session_id || null
      setCurrentSessionId(nextSessionId)
      if (!nextSessionId) {
        setLogs([])
        setHasMore(false)
      }
    }

    try {
      await apiClient.deleteSession(sessionId)

      if (wasCurrentSession) {
        const nextSessionId = nextSessions[0]?.session_id || null
        if (nextSessionId) {
          await loadSessionLogs(nextSessionId)
        }
      }

      apiClient.clearCache()
      await loadSessions(false)
      showToast('Session deleted', 'success')
      return true
    } catch (error) {
      // Roll back
      setSessions(previousSessions)
      setCurrentSessionId(previousCurrentSessionId)
      if (wasCurrentSession && previousCurrentSessionId) {
        await loadSessionLogs(previousCurrentSessionId)
      }

      showToast(error.message?.toLowerCase().includes('not found') ? 'Session not found' : 'Failed to delete session', 'error')
      return false
    }
  }, [sessions, currentSessionId, loadSessionLogs, loadSessions, showToast])

  // Delete multiple sessions (Batch)
  const deleteSessions = useCallback(async (sessionIds) => {
    if (!Array.isArray(sessionIds) || sessionIds.length === 0) return false

    const previousSessions = sessions
    const previousCurrentSessionId = currentSessionId
    const wasCurrentDeleted = sessionIds.includes(currentSessionId)
    const nextSessions = sessions.filter(session => !sessionIds.includes(session.session_id))

    // Optimistic update
    setSessions(nextSessions)

    if (wasCurrentDeleted) {
      const nextSessionId = nextSessions[0]?.session_id || null
      setCurrentSessionId(nextSessionId)
      if (!nextSessionId) {
        setLogs([])
        setHasMore(false)
      }
    }

    try {
      await apiClient.deleteSessions(sessionIds)

      if (wasCurrentDeleted) {
        const nextSessionId = nextSessions[0]?.session_id || null
        if (nextSessionId) {
          await loadSessionLogs(nextSessionId)
        }
      }

      apiClient.clearCache()
      await loadSessions(false)
      showToast(`Deleted ${sessionIds.length} session${sessionIds.length > 1 ? 's' : ''}`, 'success')
      return true
    } catch {
      setSessions(previousSessions)
      setCurrentSessionId(previousCurrentSessionId)
      if (wasCurrentDeleted && previousCurrentSessionId) {
        await loadSessionLogs(previousCurrentSessionId)
      }
      showToast('Failed to delete sessions', 'error')
      return false
    }
  }, [sessions, currentSessionId, loadSessionLogs, loadSessions, showToast])

  // Add new logs (for live real-time stream)
  const addNewLogs = useCallback((newLogs) => {
    if (!Array.isArray(newLogs) || newLogs.length === 0) return
    setLogs(prev => {
      const existingIds = new Set(prev.map(l => l.id))
      const uniqueNew = newLogs.filter(l => !existingIds.has(l.id))
      if (uniqueNew.length === 0) return prev
      return [...prev, ...uniqueNew]
    })
  }, [])

  // 1. Live Streaming (WebSocket & SSE with fallback)
  useEffect(() => {
    if (!settings.liveUpdates || !currentSessionId) {
      setStreamMode('offline')
      return
    }

    const cleanupStream = apiClient.connectStream(
      currentSessionId,
      (incomingLogs) => {
        addNewLogs(incomingLogs)
        setIsConnected(true)
        setLastUpdate(new Date())
      },
      (status) => {
        setIsConnected(status.connected)
        setStreamMode(status.mode)
      }
    )

    return () => {
      cleanupStream()
    }
  }, [currentSessionId, settings.liveUpdates, addNewLogs])

  // 2. Fast fallback polling (active when in polling mode or disconnected)
  useEffect(() => {
    if (!settings.liveUpdates) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      return
    }

    // Only run polling if streamMode is polling or disconnected
    if (streamMode === 'websocket' || streamMode === 'sse') {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      return
    }

    const pollLogs = async () => {
      const activeSession = currentSessionIdRef.current
      if (!activeSession) return

      try {
        const currentLogs = logsRef.current
        const latestLog = currentLogs[currentLogs.length - 1]
        const afterId = latestLog ? latestLog.id : null
        
        const result = await apiClient.fetchSessionLogs(activeSession, afterId ? { afterId } : {})
        const newLogs = result?.logs || []
        if (newLogs.length > 0) {
          addNewLogs(newLogs)
          setIsConnected(true)
          setLastUpdate(new Date())
        }
      } catch (error) {
        console.error('Realtime log poll error:', error)
      }
    }

    const intervalTime = Math.max(800, settings.updateInterval || 1000)
    pollIntervalRef.current = setInterval(pollLogs, intervalTime)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [settings.liveUpdates, settings.updateInterval, streamMode, addNewLogs])

  // Periodic sessions list refresh (every 10s)
  useEffect(() => {
    const pollSessions = async () => {
      try {
        const sessionsResult = await apiClient.fetchSessions(0, 50, false)
        const newSessions = sessionsResult?.sessions || []
        if (Array.isArray(newSessions) && newSessions.length > 0) {
          setSessions(newSessions)
        }
      } catch (err) {
        console.error('Sessions poll error:', err)
      }
    }

    sessionsPollRef.current = setInterval(pollSessions, 10000)

    return () => {
      if (sessionsPollRef.current) {
        clearInterval(sessionsPollRef.current)
      }
    }
  }, [])

  // Initial load
  useEffect(() => {
    const init = async () => {
      const loadedSessions = await loadSessions(false)
      if (loadedSessions.length > 0) {
        await selectSession(loadedSessions[0].session_id)
      }
    }
    init()
  }, [])

  const value = {
    sessions,
    currentSessionId,
    logs,
    isLoading,
    isLoadingMore,
    isConnected,
    streamMode,
    lastUpdate,
    loadSessions,
    selectSession,
    deleteSession,
    deleteSessions,
    refresh,
    setLogs,
    hasMore,
    loadMoreLogs
  }

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}
